// Sales as they happen.
//
// The store announces every sale, refund, ledger row and order change on
// `/rt/store` (aldoo-na/store/broadcast). A screen that lists any of those
// subscribes here and refetches when it hears one: the notice carries ids and
// an amount, never the rows themselves, so what a screen shows is still what
// its own API call is allowed to give it.
//
//   useStoreEvents({ orgId, onEvent })            every event of one seller
//   useStoreEvents({ onEvent: refresh })          the platform's, for an admin
//   useStoreEvents({ mine: true, onEvent })       a buyer's own orders (their orders page)
//
// Without an orgId the server only admits whoever administers the platform;
// anyone else is quietly not subscribed and the screen behaves as it did
// before — refetching when the user asks.
import { useEffect, useRef } from "react"
import { useRealtime } from "aldoo-ra/Realtime/hooks"

export const STORE_EVENTS = ["store:sale", "store:refund", "store:transaction", "store:order"]

export default function useStoreEvents({ orgId = null, mine = false, events = STORE_EVENTS, onEvent, enabled = true } = {}) {
  const { socket, connected } = useRealtime(enabled ? "store" : null)
  // the handler changes on every render of the caller; the subscription must not
  const handler = useRef(onEvent)
  useEffect(() => {
    handler.current = onEvent
  }, [onEvent])

  useEffect(() => {
    if (!socket || !connected || !enabled) return

    const room = mine ? { mine: true } : { orgId }
    socket.emit("store:watch", room)
    const listeners = events.map((event) => {
      const listener = (payload) => handler.current?.({ ...payload, event })
      socket.on(event, listener)
      return () => socket.off(event, listener)
    })

    return () => {
      socket.emit("store:unwatch", room)
      listeners.forEach((off) => off())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, connected, enabled, orgId, mine, events.join(",")])

  return { connected }
}
